// After "Import" (F9b B5): the runs of a confirmed plan, one row per Higgsfield project, with live counts per kind
// (folders, elements, takes), the failed lines with their reason and Retry, and per production "What came in".
// Calm: a work step, no loud moment, white progress lines (no department colour), money never shows a price here:
// the work was made, and paid for, in Higgsfield. A plan staged through Connect Higgsfield (via "higgsfield") is read
// by Filmsquare itself: no sentence for Claude, and a quiet note with Try again or Reconnect when reading did not start.

import { useEffect, useMemo, useRef, useState } from "react";
import { BRAND } from "../../brand.ts";
import {
  type HiggsfieldApi,
  onHiggsfieldReturn,
  takeHiggsfieldReconnect,
} from "../../data/higgsfield-api.ts";
import {
  continueSentence,
  type ImportsApi,
  type KindCounts,
  type Plan,
  type PlanItem,
  type RunView,
  type SourceKind,
} from "../../data/imports-api.ts";
import { docsUrl, IMPORT_DOCS } from "../../docs.ts";
import { currentLocale } from "../../locale.ts";
import { formatRoute } from "../../route.ts";
import { errText } from "../app.ts";
import { CopyText } from "../connect/CopyText.tsx";
import { Icon } from "../icons.tsx";
import { Button, Dialog, LinkButton } from "../kit.tsx";
import { ConnectButton, HiggsfieldReadNote, ReadInstead } from "./HiggsfieldConnect.tsx";
import { WhatCameIn } from "./WhatCameIn.tsx";

const POLL_MS = 2500;

type Views = Map<string, RunView | { error: string }>;

/** A plan-wide Read again reopens every failed Higgsfield run, so none may stay cached as terminal. */
export function viewsAfterReadAgain(views: Views): Views {
  return new Map([...views].filter(([, view]) => !("run" in view && view.run.status === "failed")));
}

const total = (c: KindCounts | undefined) => (c ? c.queued + c.done + c.skipped + c.failed : 0);
const settled = (c: KindCounts | undefined) => (c ? c.done + c.skipped + c.failed : 0);

/** Where a plan item went, in words: "Sherlock · Episode 8", or the production's name. */
export function destinationText(item: PlanItem): string {
  const t = item.target;
  if (t.kind === "standalone") return t.name;
  const series = "newKey" in t.series ? t.series.name : null;
  return series ? `${series} · Episode ${t.number}` : `Episode ${t.number}`;
}

/** Who brings the files: the person's own Claude, or Filmsquare reading Higgsfield with their connection. */
export type Reader = "claude" | "filmsquare";

/** The reader of a plan: Filmsquare for a plan staged through Connect Higgsfield. */
export const readerOf = (plan: { via: string }): Reader =>
  plan.via === "higgsfield" ? "filmsquare" : "claude";

/**
 * Who reads this plan now: Filmsquare for a plan staged through Connect Higgsfield, and also for a plan Claude staged
 * once Filmsquare took the read over (on this screen, or earlier: a run read with a connection says so).
 */
export function readerFor(
  plan: { via: string },
  runs: readonly (Pick<RunView["run"], "readFromHiggsfield"> | undefined)[],
  switched = false,
): Reader {
  if (readerOf(plan) === "filmsquare" || switched) return "filmsquare";
  return runs.some((r) => r?.readFromHiggsfield) ? "filmsquare" : "claude";
}

export function failedRunState(
  run: Pick<RunView["run"], "status" | "error">,
  reader: Reader,
): { reason: string | null; readAgain: boolean; reconnect: boolean } {
  const reason = run.status === "failed" ? run.error : null;
  const readAgain = reader === "filmsquare" && run.status === "failed";
  return {
    reason,
    readAgain,
    reconnect:
      readAgain &&
      !!reason &&
      /higgsfield_reconnect|needs_reconnect|connect Higgsfield again|connection.*no longer/i.test(reason),
  };
}

/**
 * Back from Reconnect Higgsfield on this screen: the stopped runs are read again by themselves (the person already
 * said what they want). "wait" while a run is still loading, "read" when Filmsquare reads and a run stopped.
 */
export function afterReconnect(rows: (RunView | undefined)[], reader: Reader): "read" | "wait" | "none" {
  if (rows.some((row) => !row)) return "wait";
  return reader === "filmsquare" && rows.some((row) => row?.run.status === "failed") ? "read" : "none";
}

export interface RunState {
  label: string;
  /** Polling may stop for this run. */
  terminal: boolean;
  /** Successfully completed; controls check styling. */
  done: boolean;
  failed: boolean;
}

/** One run's state in plain words, with failure distinct from completion. */
export function runState(v: RunView, reader: Reader = "claude"): RunState {
  const kinds: SourceKind[] = ["folder", "element", "asset"];
  const all = kinds.reduce((n, k) => n + total(v.counts[k]), 0);
  const failed = kinds.reduce((n, k) => n + (v.counts[k]?.failed ?? 0), 0);
  // A run Filmsquare reads is not in until the read finished: a Claude "finish" (racing the read, or from before the
  // take-over) can mark it done while pages still come. A reader that stops is failed by the server's repair sweep.
  const reading = v.run.readFromHiggsfield === true && !v.run.read_finished_at;
  switch (reading && v.run.status === "done" ? "open" : v.run.status) {
    case "open":
      if (reader === "filmsquare")
        return {
          label: all === 0 ? "Waiting for Higgsfield" : "Reading Higgsfield",
          terminal: false,
          done: false,
          failed: false,
        };
      return {
        label: all === 0 ? "Waiting for your Claude" : "Your Claude is sending the files",
        terminal: false,
        done: false,
        failed: false,
      };
    case "running":
      return { label: "Copying in", terminal: false, done: false, failed: false };
    case "failed":
      return { label: "Stopped", terminal: true, done: false, failed: true };
    case "done":
      return {
        label: failed ? `In, ${failed} ${failed === 1 ? "line" : "lines"} failed` : "In",
        terminal: true,
        done: true,
        failed: false,
      };
  }
}

const runImported = (v: RunView): boolean =>
  (["folder", "element", "asset"] as const).some((kind) => (v.counts[kind]?.done ?? 0) > 0);

/** Plan-wide terminal/outcome classification used by the dialog title and actions. */
export function classifyImportProgress(rows: (RunView | undefined)[], reader: Reader = "claude") {
  const states = rows.map((row) => (row ? runState(row, reader) : null));
  const settled = rows.length > 0 && states.every((state) => state?.terminal === true);
  const completed = states.filter((state) => state?.done).length;
  const failed = states.filter((state) => state?.failed).length;
  return {
    settled,
    completed,
    failed,
    allDone: settled && failed === 0,
    allFailed: settled && completed === 0,
    anyImported: rows.some((row) => row && runImported(row)),
  };
}

/**
 * Run `once` now, and again `delayMs` after each answer, until it says it is done or the returned stop is called.
 * A failing round counts as not done. Timers are injectable for tests.
 */
export function startPolling(
  once: () => Promise<boolean>,
  delayMs: number,
  timers: { set: (f: () => void, ms: number) => unknown; clear: (t: unknown) => void } = {
    set: (f, ms) => setTimeout(f, ms),
    clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  },
): () => void {
  let stopped = false;
  let timer: unknown;
  const step = async () => {
    let done = false;
    try {
      done = await once();
    } catch {
      done = false;
    }
    if (!stopped && !done) timer = timers.set(() => void step(), delayMs);
  };
  void step();
  return () => {
    stopped = true;
    if (timer !== undefined) timers.clear(timer);
  };
}

/**
 * The dialog title: "in" only when every run finished; a stopped run says so instead, and once everything settled
 * with some runs in and some stopped, it says some of it is in.
 */
export function importTitle(allDone: boolean, anyStopped: boolean, projects: number, someIn = false): string {
  if (allDone) return "The import is in";
  if (anyStopped) return someIn ? "Some of the import is in" : "The import stopped";
  return `Importing ${projects} ${projects === 1 ? "project" : "projects"}`;
}

/** For a plan Filmsquare reads itself: the connection, why reading did not start, and how to start it. */
export interface ReadControl {
  hf: HiggsfieldApi;
  /** the read-from-higgsfield call failed; null while it did not (or has not run) */
  error: unknown;
  retry: () => Promise<void>;
  /** a run still waits: start reading if this screen has not yet (idempotent on the server) */
  onWaiting: () => void;
}

export function ImportProgress({
  im,
  plan,
  onClose,
  read,
}: {
  im: ImportsApi;
  plan: Plan;
  onClose: () => void;
  read?: ReadControl;
}) {
  const [views, setViews] = useState<Views>(new Map());
  // Filmsquare took over reading a plan Claude staged, on this screen (an earlier take-over shows on the runs)
  const [switched, setSwitched] = useState(false);
  // a retry starts the chain again
  const [kick, setKick] = useState(0);
  const items = useMemo(() => [...plan.items].sort((a, b) => a.position - b.position), [plan.items]);
  const runIds = items.flatMap((i) => (i.run_id ? [i.run_id] : []));
  const reader = readerFor(
    plan,
    runIds.map((id) => {
      const v = views.get(id);
      return v && "run" in v ? v.run : undefined;
    }),
    switched,
  );
  const isTerminal = (v: RunView | { error: string } | undefined) =>
    !!v && "run" in v && runState(v, reader).terminal;
  const progress = classifyImportProgress(
    runIds.map((id) => {
      const view = views.get(id);
      return view && "run" in view ? view : undefined;
    }),
    reader,
  );
  const viewsRef = useRef(views);
  viewsRef.current = views;
  // One poll after the other: the next is set only when the last one answered, so a slow server is never
  // asked again before it replied (and a hidden tab skips its turn).
  // biome-ignore lint/correctness/useExhaustiveDependencies: the run ids are fixed for a plan; kick restarts
  useEffect(() => {
    let live = true;
    const stop = startPolling(async () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return false;
      const pending = runIds.filter((id) => !isTerminal(viewsRef.current.get(id)));
      if (pending.length === 0) return true;
      const rows = await Promise.all(
        pending.map((id) =>
          im.run(id).then(
            (v) => [id, v] as const,
            (e) => [id, { error: errText(e) }] as const,
          ),
        ),
      );
      if (!live) return true;
      const next = new Map(viewsRef.current);
      for (const [id, v] of rows) if ("run" in v || !next.has(id)) next.set(id, v);
      viewsRef.current = next;
      setViews(next);
      return runIds.every((id) => isTerminal(next.get(id)));
    }, POLL_MS);
    return () => {
      live = false;
      stop();
    };
  }, [im, plan.id, kick]);

  const doneCount = progress.completed;
  const waiting = runIds.some((id) => {
    const v = views.get(id);
    return v && "run" in v && v.run.status === "open";
  });
  const onWaiting = read?.onWaiting;
  useEffect(() => {
    if (waiting && onWaiting && reader === "filmsquare") onWaiting();
  }, [waiting, onWaiting, reader]);
  const [open, setOpen] = useState<string | null>(null);

  const readAgain =
    reader === "filmsquare" && read
      ? async () => {
          await read.retry();
          setViews(viewsAfterReadAgain);
          setKick((n) => n + 1);
        }
      : undefined;
  // a sign-in that came back connected to this screen, taken once (also when it finished before the plan loaded)
  const [resume, setResume] = useState(false);
  useEffect(() => {
    const check = () => {
      if (takeHiggsfieldReconnect(window.location.hash)) setResume(true);
    };
    check();
    return onHiggsfieldReturn(check);
  }, []);
  const rowsForResume = runIds.map((id) => {
    const view = views.get(id);
    return view && "run" in view ? view : undefined;
  });
  const resumeNow = resume ? afterReconnect(rowsForResume, reader) : "none";
  // biome-ignore lint/correctness/useExhaustiveDependencies: decided once the runs are in; readAgain is per render
  useEffect(() => {
    if (!resume || resumeNow === "wait") return;
    setResume(false);
    if (resumeNow === "read" && readAgain) void readAgain();
  }, [resume, resumeNow]);

  return (
    <Dialog
      title={importTitle(
        progress.allDone,
        progress.failed > 0,
        items.length,
        progress.settled && progress.completed > 0,
      )}
      eyebrow={<span className="imp-eyebrow">2.0 · Import</span>}
      lead={
        <span className="imp-lead">
          <span role="status">
            {doneCount} of {runIds.length} in
          </span>
          <span>Made in Higgsfield · no credits used</span>
        </span>
      }
      onClose={onClose}
      className="imp"
      footer={
        <div className="imp-foot">
          <div className="imp-foot-text">
            <p className="imp-foot-sum">
              <span className="imp-foot-free">
                {progress.allDone
                  ? "Folders stay folders. Make shots from a folder when you are ready."
                  : progress.failed > 0
                    ? reader === "filmsquare" && progress.allFailed
                      ? "Nothing came in. Reconnect or read the projects again."
                      : progress.settled && progress.completed > 0
                        ? "Some projects stopped. Their rows show what to fix or retry."
                        : "A run stopped before it finished. Retry carries on where it left off."
                    : "You can close this. The import carries on, and this screen picks it up again."}
              </span>
            </p>
            <a
              className="imp-foot-docs"
              href={docsUrl(IMPORT_DOCS, currentLocale())}
              target="_blank"
              rel="noopener"
            >
              How the import works
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
          {progress.settled ? (
            <>
              <Button variant="text" onClick={onClose}>
                Close
              </Button>
              {progress.anyImported && (
                <LinkButton href={formatRoute({ view: "productions" })} variant="primary" size="lg">
                  Open productions
                </LinkButton>
              )}
            </>
          ) : (
            <Button onClick={onClose}>Close</Button>
          )}
        </div>
      }
    >
      <div className="imp-body">
        <p className="imp-progress-lead">
          {reader === "filmsquare"
            ? `${BRAND.name} reads your Higgsfield projects with your connection, read-only, and copies every file into your storage.`
            : `Your Claude sends the files from Higgsfield; ${BRAND.name} copies every one into your storage.`}{" "}
          Nothing is generated and nothing is overwritten.
        </p>
        {read && read.error != null && (
          <HiggsfieldReadNote hf={read.hf} error={read.error} onRetry={read.retry} />
        )}
        {waiting && reader === "claude" && (
          <div className="imp-claude">
            <p className="imp-claude-text">
              Waiting for your Claude. If it stopped, paste this into the same chat to carry on:
            </p>
            <CopyText
              prose
              label="Sentence for your Claude"
              text={continueSentence(plan.id)}
              shown={continueSentence(null)}
            />
            {read && plan.source_workspace_id && (
              <ReadInstead
                onRead={async () => {
                  await read.hf.read(plan.id);
                  setSwitched(true);
                  // the server reads every run of the plan, finished ones too: forget them all so the polling follows
                  setViews(new Map());
                  setKick((n) => n + 1);
                }}
              />
            )}
          </div>
        )}
        <ul className="imp-runs">
          {items.map((it) => (
            <RunRow
              key={it.id}
              item={it}
              reader={reader}
              view={it.run_id ? (views.get(it.run_id) ?? null) : null}
              open={open === it.id}
              onToggle={() => setOpen((o) => (o === it.id ? null : it.id))}
              onRetry={async () => {
                if (!it.run_id) return;
                await im.retry(it.run_id);
                setViews((m) => {
                  const next = new Map(m);
                  next.delete(it.run_id!);
                  return next;
                });
                setKick((n) => n + 1);
              }}
              hf={read?.hf}
              onReadAgain={readAgain}
            />
          ))}
        </ul>
      </div>
    </Dialog>
  );
}

const KIND_LABEL: Record<SourceKind, string> = { folder: "Folders", element: "Elements", asset: "Takes" };

export function RunRow({
  item,
  reader = "claude",
  view,
  open,
  onToggle,
  onRetry,
  hf,
  onReadAgain,
}: {
  item: PlanItem;
  reader?: Reader;
  view: RunView | { error: string } | null;
  open: boolean;
  onToggle: () => void;
  onRetry: () => Promise<void>;
  hf?: HiggsfieldApi;
  onReadAgain?: () => Promise<void>;
}) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const v = view && "run" in view ? view : null;
  const st = v ? runState(v, reader) : null;
  const failed = v?.failed ?? [];
  const assets = v?.counts.asset;
  const pct = assets && total(assets) > 0 ? Math.round((settled(assets) / total(assets)) * 100) : 0;
  const recovery = v ? failedRunState(v.run, reader) : null;
  return (
    <li className={`imp-run${st?.done ? " done" : st?.failed ? " failed" : ""}`}>
      <div className="imp-run-head">
        <span className="imp-run-name">
          <span className="imp-run-src">{item.name}</span>
          <Icon name="arrow" size={12} />
          <span className="imp-run-dest">{destinationText(item)}</span>
        </span>
        <span className={`imp-run-state${st?.done ? " done" : st?.failed ? " failed" : ""}`}>
          {st?.done ? (
            <Icon name="check" size={12} />
          ) : st?.failed ? null : (
            <span className="imp-run-pulse" aria-hidden="true" />
          )}
          {view && "error" in view ? "Could not read the progress" : (st?.label ?? "Opening…")}
        </span>
      </div>
      <div
        className="imp-bar"
        role="progressbar"
        aria-label={`Takes of ${item.name}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
      {v && (
        <dl className="imp-kinds">
          {(["folder", "element", "asset"] as const).map((k) => (
            <div key={k} className="imp-kind">
              <dt>{KIND_LABEL[k]}</dt>
              <dd className="k-meta">
                {total(v.counts[k]) === 0
                  ? "—"
                  : `${settled(v.counts[k]).toLocaleString("en")} / ${total(v.counts[k]).toLocaleString("en")}`}
                {(v.counts[k]?.skipped ?? 0) > 0 && (
                  <span className="imp-kind-skip"> · {v.counts[k]!.skipped} already in</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {v && v.run.warnings.length > 0 && (
        <div className="imp-check note" role="status">
          {v.run.warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      )}
      {recovery && (recovery.reason || recovery.readAgain) && (
        <div className="imp-check note" role="status">
          <p>{recovery.reason ?? "The Higgsfield read stopped."}</p>
          {recovery.readAgain && onReadAgain && (
            <div className="imp-hf-do">
              <Button
                variant="outline"
                size="sm"
                disabled={retrying}
                aria-busy={retrying || undefined}
                onClick={async () => {
                  setRetrying(true);
                  setRetryError(null);
                  try {
                    await onReadAgain();
                  } catch (e) {
                    setRetryError(errText(e));
                  } finally {
                    setRetrying(false);
                  }
                }}
              >
                {retrying ? "Starting…" : "Read again"}
              </Button>
              {recovery.reconnect && hf && <ConnectButton hf={hf} label="Reconnect Higgsfield" />}
            </div>
          )}
          {retryError && <p className="imp-check conflict">{retryError}</p>}
        </div>
      )}
      {/* Retry re-queues the lines; a stopped run Filmsquare reads itself is started again by Read again above */}
      {(failed.length > 0 || (st?.failed && !(recovery?.readAgain && onReadAgain))) && (
        <div className="imp-failed-lines">
          <p className="imp-failed-head">
            {failed.length > 0
              ? `${failed.length} ${failed.length === 1 ? "line" : "lines"} did not come in`
              : "The import stopped before it finished"}
            <Button
              variant="outline"
              size="sm"
              disabled={retrying}
              aria-busy={retrying || undefined}
              onClick={async () => {
                setRetrying(true);
                setRetryError(null);
                try {
                  await onRetry();
                } catch (e) {
                  setRetryError(errText(e));
                } finally {
                  setRetrying(false);
                }
              }}
            >
              {retrying ? "Retrying…" : "Retry"}
            </Button>
          </p>
          {retryError && (
            <p className="imp-check conflict" role="alert">
              {retryError}
            </p>
          )}
          <ul>
            {failed.slice(0, 8).map((f) => (
              <li key={f.id}>
                <span className="k-meta">{KIND_LABEL[f.source_kind].replace(/s$/, "")}</span>
                <span className="imp-failed-why">{failReason(f.error, reader)}</span>
              </li>
            ))}
            {failed.length > 8 && <li className="k-meta">and {failed.length - 8} more</li>}
          </ul>
        </div>
      )}
      {/* a stopped run is not "in"; only a run Filmsquare reads itself resumes by what already came in */}
      {v && (st?.done || (reader === "filmsquare" && runImported(v))) && (
        <>
          <button type="button" className="imp-came-toggle" aria-expanded={open} onClick={onToggle}>
            What came in
            <Icon name="chevron" size={10} className={open ? "up" : "down"} />
          </button>
          {open && <WhatCameIn projectId={v.run.project_id} since={v.run.created_at} />}
        </>
      )}
    </li>
  );
}

/** The job's reason, in the studio's words. */
export function failReason(e: string | null, reader: Reader = "claude"): string {
  if (!e) return "No reason given";
  if (/higgsfield_reconnect|needs_reconnect/i.test(e))
    return "Higgsfield asks you to connect again. Reconnect, then retry.";
  if (/plan_limit:storage|storage/i.test(e)) return "Storage is full. Retry after more room comes with Team.";
  if (/expired|403|404|gone/i.test(e))
    return reader === "filmsquare"
      ? "The link from Higgsfield no longer works. Retry reads it again."
      : "The link from Higgsfield no longer works. Your Claude can send it again.";
  if (/too large|size/i.test(e)) return "The file is larger than an import may be.";
  if (/not media|mime|type/i.test(e)) return "Not a picture, video or sound file.";
  return e;
}
